import { useLegalDocument } from '../../hooks/useLegalDocument';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, Check, FileArchive, FileImage, FileText, Laptop, Monitor, Plus, Send, Smartphone, UploadCloud, X } from 'lucide-react';
import { Button, ConfirmDialog, Input, QuoteOptionCard, QuoteOtpInput, QuoteStepIndicator, Select, Textarea, useChunkRecoveryLanguage, useUnsavedWork } from '@mbbe/ui';
import type {
  QuoteAttachmentDraft,
  QuoteBudgetRange,
  QuoteProjectType,
  QuoteRequestDraft,
  QuoteTimeline,
  VerifyQuoteOtpOutput,
} from '@mbbe/types';
import { ApiError } from '@mbbe/config';
import { RecaptchaVerifier, signInWithPhoneNumber, type ConfirmationResult } from 'firebase/auth';
import { budgetOptions, featureOptions, projectTypes, timelineOptions } from './quoteOptions';
import { getTurnstileToken } from './turnstile';
import { generateQuoteAttachmentUpload, markQuoteSmsFailed, startQuoteRequest, verifyQuoteOtp } from './api';
import { getFirebaseAuth } from '../../lib/firebase';
import { getAttribution, type AttributionData } from '../../lib/attribution';

const firedLeadIds = new Set<string>();

export function pushVerifiedLeadSubmitEvent(
  verifyResponse: VerifyQuoteOtpOutput,
  fallback: { projectType: QuoteProjectType; budget?: QuoteBudgetRange },
  attribution: AttributionData
): boolean {
  const leadId = verifyResponse.quoteRequestId;
  if (firedLeadIds.has(leadId)) return false;

  firedLeadIds.add(leadId);
  const touch = attribution.last_touch ?? attribution.first_touch;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'verified_lead_submit',
    transaction_id: leadId,
    lead_id: leadId,
    service_type: verifyResponse.serviceType ?? null,
    project_type: verifyResponse.projectType ?? fallback.projectType,
    budget_range: verifyResponse.budgetRange ?? fallback.budget ?? null,
    sms_verified: true,
    form_step_count: 4,
    landing_page: touch?.landing_page ?? null,
    gclid: touch?.gclid ?? null,
    gbraid: touch?.gbraid ?? null,
    wbraid: touch?.wbraid ?? null,
    utm_source: touch?.utm_source ?? null,
    utm_medium: touch?.utm_medium ?? null,
    utm_campaign: touch?.utm_campaign ?? null,
    utm_term: touch?.utm_term ?? null,
  });

  return true;
}

/**
 * Grants ad/measurement consent right before the conversion event fires, so GTM's
 * consent-gated Google Ads tag sees `granted`. Only called when the user ticked the
 * optional marketing-consent checkbox. Mirrors CookieConsentBanner's applyConsent.
 */
export function grantMarketingConsentSignal(): void {
  window.dataLayer = window.dataLayer || [];
  const granted = {
    ad_storage: 'granted',
    ad_user_data: 'granted',
    ad_personalization: 'granted',
    analytics_storage: 'granted',
  };
  if (typeof window.gtag === 'function') {
    window.gtag('consent', 'update', granted);
    return;
  }
  window.dataLayer.push({ event: 'consent_update', ...granted });
}

const STORAGE_KEY = 'mbbe.quoteRequestDraft.v1';
const stepLabels = ['Proje Türü', 'Özellikler', 'Proje Özeti', 'İletişim'];
const stepContent = [
  {
    title: 'Proje Türünü Seçin',
    eyebrow: 'Proje Türü',
    description: 'Web, mobil veya masaüstü odağını seçin; sonraki adımları buna göre netleştirelim.',
  },
  {
    title: 'Gerekli Özellikleri Belirleyin',
    eyebrow: 'Özellikler',
    description: 'Hazır seçeneklerden ilerleyin veya ihtiyacınız olan özel bir özelliği listeye ekleyin.',
  },
  {
    title: 'Proje Detaylarını Paylaşın',
    eyebrow: 'Proje Özeti',
    description: 'Kapsamı, bütçeyi ve zamanlamayı paylaşın; teklif yapısını gerçek ihtiyaca göre hazırlayalım.',
  },
  {
    title: 'İletişim Bilgilerinizi Girin',
    eyebrow: 'İletişim',
    description: 'Teklif talebinizi güvenli şekilde tamamlamak için ulaşabileceğimiz bilgileri doğrulayın.',
  },
] as const;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const TURKEY_MOBILE_PATTERN = /^90(5\d{9})$/;
const CUSTOM_FEATURE_LIMIT = 5;
const CUSTOM_FEATURE_MIN_LENGTH = 2;
const CUSTOM_FEATURE_MAX_LENGTH = 80;
const DESCRIPTION_MIN_LENGTH = 25;
const DESCRIPTION_MAX_LENGTH = 1000;
const MAX_ATTACHMENT_FILES = 30;
const MAX_ATTACHMENT_SIZE = 30 * 1024 * 1024;
const ATTACHMENT_ACCEPT = 'image/jpeg,image/png,image/gif,image/webp,image/avif,application/pdf,application/zip,application/x-zip-compressed,.zip';
const ALLOWED_ATTACHMENT_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/avif',
  'application/pdf',
  'application/zip',
  'application/x-zip-compressed',
]);
const CONTROL_CHAR_PATTERN = /[\u0000-\u001F\u007F-\u009F]/g;
const MEANINGFUL_FEATURE_PATTERN = /[\p{L}\p{N}]/u;

type ModalState = 'form' | 'otp' | 'success';
type Errors = Partial<Record<keyof QuoteRequestDraft | 'attachments' | 'otp' | 'submit', string>>;
type QuoteAttachmentState = QuoteAttachmentDraft & {
  localId: string;
  progress: number;
  status: 'uploading' | 'uploaded' | 'error';
};

const emptyDraft: QuoteRequestDraft = {
  projectType: 'web',
  features: [],
  description: '',
  fullName: '',
  phone: '',
  email: '',
  companyName: '',
  consent: false,
  kvkkAcknowledged: false,
  contactAcknowledged: false,
  marketingConsent: false,
};

function loadDraft(): QuoteRequestDraft {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return emptyDraft;
    return { ...emptyDraft, ...JSON.parse(raw) };
  } catch {
    return emptyDraft;
  }
}

function projectIcon(type: QuoteProjectType) {
  if (type === 'mobile') return <Smartphone size={22} />;
  if (type === 'desktop') return <Monitor size={22} />;
  return <Laptop size={22} />;
}

function normalizeTurkishMobilePhone(value: string): string {
  const digits = value.replace(/\D/g, '');
  if (digits.startsWith('90')) return digits.slice(0, 12);
  if (digits.startsWith('0')) return `9${digits}`.slice(0, 12);
  if (digits.startsWith('5')) return `90${digits}`.slice(0, 12);
  return digits.slice(0, 12);
}

function formatTurkishMobilePhone(value: string): string {
  const normalized = normalizeTurkishMobilePhone(value);
  const local = normalized.startsWith('90') ? normalized.slice(2) : normalized;
  const parts = ['+90'];
  if (local.length > 0) parts.push(local.slice(0, 3));
  if (local.length > 3) parts.push(local.slice(3, 6));
  if (local.length > 6) parts.push(local.slice(6, 8));
  if (local.length > 8) parts.push(local.slice(8, 10));
  return parts.join(' ');
}

function isValidTurkishMobilePhone(value: string): boolean {
  return TURKEY_MOBILE_PATTERN.test(normalizeTurkishMobilePhone(value));
}

function normalizeEmail(value: string): string {
  return value.trim().toLowerCase();
}

function isValidEmail(value: string): boolean {
  const email = normalizeEmail(value);
  return EMAIL_PATTERN.test(email) && !email.includes('..');
}

function normalizeFeatureInput(value: string): string {
  return value.replace(CONTROL_CHAR_PATTERN, '').replace(/\s+/g, ' ').trim();
}

function featureIdentity(value: string): string {
  return normalizeFeatureInput(value).toLocaleLowerCase('tr-TR');
}

function isMeaningfulFeature(value: string): boolean {
  return MEANINGFUL_FEATURE_PATTERN.test(value);
}

function formatFileSize(size: number): string {
  if (size >= 1024 * 1024) return `${(size / 1024 / 1024).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(size / 1024))} KB`;
}

function isAllowedAttachment(file: File): boolean {
  if (ALLOWED_ATTACHMENT_TYPES.has(file.type)) return true;
  return file.name.toLocaleLowerCase('tr-TR').endsWith('.zip') && file.size > 0;
}

function attachmentIcon(mimeType: string) {
  if (mimeType.startsWith('image/')) return <FileImage size={18} />;
  if (mimeType === 'application/pdf') return <FileText size={18} />;
  return <FileArchive size={18} />;
}

function firebaseErrorMessage(code: string): string {
  switch (code) {
    case 'auth/invalid-phone-number':
      return 'Lütfen geçerli bir telefon numarası girin.';
    case 'auth/invalid-verification-code':
      return 'Doğrulama kodu hatalı veya süresi dolmuş. Lütfen tekrar deneyin.';
    case 'auth/code-expired':
      return 'Doğrulama kodu hatalı veya süresi dolmuş. Lütfen tekrar deneyin.';
    case 'auth/too-many-requests':
      return 'Çok fazla deneme yapıldı. Lütfen bir süre bekleyin.';
    case 'auth/missing-verification-code':
      return '6 haneli kodu eksiksiz girin.';
    case 'auth/session-expired':
      return 'Oturum süresi doldu. Lütfen tekrar başlatın.';
    default:
      return 'SMS doğrulaması başarısız oldu. Lütfen tekrar deneyin.';
  }
}

export interface QuoteRequestModalProps {
  open: boolean;
  onClose: () => void;
}

const QuoteRequestModal: React.FC<QuoteRequestModalProps> = ({ open, onClose }) => {
  const legalNotice = useLegalDocument('kvkk-tr', open);
  const [draft, setDraft] = useState<QuoteRequestDraft>(() => ({ ...loadDraft(), kvkkAcknowledged: false, contactAcknowledged: false }));
  const acceptedLegalVersion = useRef<string | undefined>(undefined);
  const [step, setStep] = useState(1);
  const [errors, setErrors] = useState<Errors>({});
  const [modalState, setModalState] = useState<ModalState>('form');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [quoteRequestId, setQuoteRequestId] = useState('');
  const [otp, setOtp] = useState('');
  const [featureQuery, setFeatureQuery] = useState('');
  const [confirmationResult, setConfirmationResult] = useState<ConfirmationResult | null>(null);
  const [pendingPhone, setPendingPhone] = useState('');
  const [contentHeight, setContentHeight] = useState<number | undefined>();
  const [attachments, setAttachments] = useState<QuoteAttachmentState[]>([]);
  const [isDragActive, setIsDragActive] = useState(false);
  const [phoneConfirmOpen, setPhoneConfirmOpen] = useState(false);

  const attachmentSessionIdRef = useRef(crypto.randomUUID());
  const phoneInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const animatedContentRef = useRef<HTMLDivElement | null>(null);
  const recaptchaContainerRef = useRef<HTMLDivElement | null>(null);
  const recaptchaVerifierRef = useRef<RecaptchaVerifier | null>(null);
  const turnstileRef = useRef<HTMLDivElement | null>(null);

  const selectedFeatures = featureOptions[draft.projectType];
  const selectedFeatureIdentities = new Set(selectedFeatures.map((feature) => featureIdentity(feature.label)));
  const selectedCustomFeatures = draft.features.filter((feature) => !selectedFeatureIdentities.has(featureIdentity(feature)));
  const normalizedFeatureQuery = normalizeFeatureInput(featureQuery);
  const normalizedFeatureQueryIdentity = featureIdentity(normalizedFeatureQuery);
  const visibleFeatures = selectedFeatures.filter((feature) =>
    featureIdentity(feature.label).includes(normalizedFeatureQueryIdentity)
  );
  const customFeatureError = (() => {
    if (!normalizedFeatureQuery) return undefined;
    if (normalizedFeatureQuery.length < CUSTOM_FEATURE_MIN_LENGTH) return 'Özellik adı en az 2 karakter olmalı.';
    if (normalizedFeatureQuery.length > CUSTOM_FEATURE_MAX_LENGTH) return 'Özellik adı en fazla 80 karakter olmalı.';
    if (!isMeaningfulFeature(normalizedFeatureQuery)) return 'Özellik adı en az bir harf veya rakam içermeli.';
    if (draft.features.some((feature) => featureIdentity(feature) === normalizedFeatureQueryIdentity)) {
      return 'Bu özellik zaten seçili.';
    }
    if (selectedCustomFeatures.length >= CUSTOM_FEATURE_LIMIT) return 'En fazla 5 özel özellik ekleyebilirsiniz.';
    return undefined;
  })();
  const canAddCustomFeature = visibleFeatures.length === 0 && normalizedFeatureQuery.length > 0 && !customFeatureError;

  useEffect(() => {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(draft));
  }, [draft]);

  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  useLayoutEffect(() => {
    const content = animatedContentRef.current;
    if (!content) return undefined;

    const updateHeight = () => {
      setContentHeight(content.getBoundingClientRect().height);
    };

    updateHeight();
    if (typeof ResizeObserver === 'undefined') return undefined;

    let resizeObserver: ResizeObserver;
    try {
      resizeObserver = new ResizeObserver(updateHeight);
    } catch {
      return undefined;
    }
    resizeObserver.observe(content);
    return () => resizeObserver.disconnect();
  }, [modalState, step, errors]);

  const destroyRecaptcha = useCallback(() => {
    if (recaptchaVerifierRef.current) {
      try {
        recaptchaVerifierRef.current.clear();
      } catch {
        // ignore
      }
      recaptchaVerifierRef.current = null;
    }
    if (recaptchaContainerRef.current) {
      recaptchaContainerRef.current.replaceChildren();
    }
  }, []);

  useEffect(() => {
    if (open) return undefined;
    destroyRecaptcha();
    return undefined;
  }, [destroyRecaptcha, open]);

  useEffect(() => () => destroyRecaptcha(), [destroyRecaptcha]);

  const createRecaptchaVerifier = useCallback((): RecaptchaVerifier => {
    if (recaptchaVerifierRef.current) return recaptchaVerifierRef.current;
    const recaptchaHost = recaptchaContainerRef.current;
    if (!recaptchaHost) {
      throw new Error('SMS doğrulama alanı hazırlanamadı. Lütfen tekrar deneyin.');
    }
    recaptchaHost.replaceChildren();
    const recaptchaContainer = document.createElement('div');
    recaptchaContainer.id = `firebase-recaptcha-container-${crypto.randomUUID()}`;
    recaptchaHost.appendChild(recaptchaContainer);
    const auth = getFirebaseAuth();
    const verifier = new RecaptchaVerifier(auth, recaptchaContainer, {
      size: 'invisible',
      callback: () => {
        // reCAPTCHA solved
      },
    });
    recaptchaVerifierRef.current = verifier;
    return verifier;
  }, []);

  const setField = <K extends keyof QuoteRequestDraft>(field: K, value: QuoteRequestDraft[K]) => {
    setDraft((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: undefined, submit: undefined }));
  };

  // Single combined consent checkbox drives both legal acknowledgements (KVKK + contact).
  const setConsentAcknowledged = (checked: boolean) => {
    acceptedLegalVersion.current = checked ? legalNotice.document?.version : undefined;
    setDraft((current) => ({ ...current, kvkkAcknowledged: checked, contactAcknowledged: checked }));
    setErrors((current) => ({
      ...current,
      kvkkAcknowledged: undefined,
      contactAcknowledged: undefined,
      submit: undefined,
    }));
  };

  const validateStep = (targetStep = step) => {
    const nextErrors: Errors = {};
    if (targetStep === 1 && !draft.projectType) nextErrors.projectType = 'Proje türü seçin.';
    if (targetStep === 2 && draft.features.length === 0) nextErrors.features = 'En az bir özellik seçin.';
    if (targetStep === 3 && draft.description.trim().length < DESCRIPTION_MIN_LENGTH) {
      nextErrors.description = 'Projenizi en az 25 karakterle özetleyin.';
    }
    if (targetStep === 3) {
      if (draft.description.trim().length > DESCRIPTION_MAX_LENGTH) {
        nextErrors.description = 'Proje özeti en fazla 1000 karakter olabilir.';
      }
      if (attachments.some((attachment) => attachment.status === 'uploading')) {
        nextErrors.attachments = 'Dosya yüklemeleri tamamlanmadan devam edemezsiniz.';
      }
      if (attachments.some((attachment) => attachment.status === 'error')) {
        nextErrors.attachments = 'Hatalı dosyaları kaldırın veya tekrar yükleyin.';
      }
      if (!draft.budget) nextErrors.budget = 'Tahmini bütçe seçin.';
      if (!draft.timeline) nextErrors.timeline = 'Başlangıç zamanı seçin.';
    }
    if (targetStep === 4) {
      if (draft.fullName.trim().length < 2) nextErrors.fullName = 'Ad soyad gerekli.';
      if (!isValidTurkishMobilePhone(draft.phone)) nextErrors.phone = 'Lütfen geçerli bir telefon numarası girin.';
      if (!isValidEmail(draft.email)) nextErrors.email = 'Lütfen geçerli bir e-posta adresi girin.';
      if (!draft.kvkkAcknowledged || !draft.contactAcknowledged) {
        nextErrors.kvkkAcknowledged =
          "Devam etmek için kişisel verilerinizin işlenmesi, telefonunuzun doğrulanması ve sizinle iletişime geçilmesi onayını işaretlemelisiniz.";
      }
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const canGoNext = () => {
    if (!validateStep()) return;
    setStep((current) => Math.min(4, current + 1));
  };

  const toggleFeature = (feature: string) => {
    setDraft((current) => ({
      ...current,
      features: current.features.includes(feature)
        ? current.features.filter((item) => item !== feature)
        : [...current.features, feature],
    }));
    setErrors((current) => ({ ...current, features: undefined, submit: undefined }));
  };

  const addCustomFeature = () => {
    const featureToAdd = normalizeFeatureInput(featureQuery);
    const featureToAddIdentity = featureIdentity(featureToAdd);
    if (
      !featureToAdd ||
      featureToAdd.length < CUSTOM_FEATURE_MIN_LENGTH ||
      featureToAdd.length > CUSTOM_FEATURE_MAX_LENGTH ||
      !isMeaningfulFeature(featureToAdd)
    ) {
      return;
    }

    setDraft((current) => {
      const currentOptionIdentities = new Set(
        featureOptions[current.projectType].map((feature) => featureIdentity(feature.label))
      );
      const currentCustomFeatureCount = current.features.filter(
        (feature) => !currentOptionIdentities.has(featureIdentity(feature))
      ).length;

      if (
        current.features.some((feature) => featureIdentity(feature) === featureToAddIdentity) ||
        currentCustomFeatureCount >= CUSTOM_FEATURE_LIMIT
      ) {
        return current;
      }

      return {
        ...current,
        features: [...current.features, featureToAdd],
      };
    });
    setFeatureQuery('');
    setErrors((current) => ({ ...current, features: undefined, submit: undefined }));
  };

  const handlePhoneChange = (value: string) => {
    setField('phone', formatTurkishMobilePhone(value));
  };

  const handleEmailBlur = () => {
    setField('email', normalizeEmail(draft.email));
  };

  const uploadAttachment = useCallback(async (localId: string, file: File) => {
    try {
      const fileType = file.type || (file.name.toLocaleLowerCase('tr-TR').endsWith('.zip') ? 'application/zip' : '');
      const presign = await generateQuoteAttachmentUpload({
        sessionId: attachmentSessionIdRef.current,
        attachmentId: localId,
        filename: file.name,
        fileType,
        fileSize: file.size,
      });

      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', presign.uploadUrl);
        xhr.setRequestHeader('Content-Type', fileType);
        xhr.upload.onprogress = (event) => {
          if (!event.lengthComputable) return;
          const progress = Math.round((event.loaded / event.total) * 100);
          setAttachments((current) =>
            current.map((attachment) => (attachment.localId === localId ? { ...attachment, progress } : attachment))
          );
        };
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve();
          } else {
            const body = typeof xhr.responseText === 'string' ? xhr.responseText.slice(0, 500) : '';
            const statusText = xhr.statusText ? ` ${xhr.statusText}` : '';
            reject(new Error(`R2 upload failed: status=${xhr.status}${statusText}${body ? ` body=${body}` : ''}`));
          }
        };
        xhr.onerror = () => reject(new Error('Network error'));
        xhr.send(file);
      });

      setAttachments((current) =>
        current.map((attachment) =>
          attachment.localId === localId
            ? {
                ...attachment,
                id: presign.id,
                key: presign.key,
                sessionId: presign.sessionId,
                filename: file.name,
                size: file.size,
                mimeType: fileType,
                progress: 100,
                status: 'uploaded',
              }
            : attachment
        )
      );
      setErrors((current) => ({ ...current, attachments: undefined, submit: undefined }));
    } catch (error) {
      setAttachments((current) =>
        current.map((attachment) =>
          attachment.localId === localId ? { ...attachment, progress: 0, status: 'error' } : attachment
        )
      );
      // A server refusal (pending-upload cap, presign rate limit, limiter busy) explains itself.
      const message = error instanceof ApiError && error.message !== 'Request failed'
        ? error.message
        : 'Bazı dosyalar yüklenemedi. Lütfen tekrar deneyin.';
      setErrors((current) => ({ ...current, attachments: message }));
    }
  }, []);

  const addAttachmentFiles = useCallback((files: File[]) => {
    setErrors((current) => ({ ...current, attachments: undefined, submit: undefined }));

    const remainingSlots = MAX_ATTACHMENT_FILES - attachments.length;
    if (remainingSlots <= 0) {
      setErrors((current) => ({ ...current, attachments: 'En fazla 30 dosya yükleyebilirsiniz.' }));
      return;
    }

    const acceptedFiles: File[] = [];
    let rejected = false;
    for (const file of files.slice(0, remainingSlots)) {
      if (file.size <= 0 || file.size > MAX_ATTACHMENT_SIZE || !isAllowedAttachment(file)) {
        rejected = true;
        continue;
      }
      acceptedFiles.push(file);
    }

    if (files.length > remainingSlots || rejected) {
      setErrors((current) => ({
        ...current,
        attachments: 'Yalnızca görsel, PDF veya ZIP dosyaları kabul edilir; dosya başına sınır 30 MB ve toplam sınır 30 dosyadır.',
      }));
    }

    if (acceptedFiles.length === 0) return;

    const nextAttachments = acceptedFiles.map((file) => {
      const localId = crypto.randomUUID();
      const fileType = file.type || (file.name.toLocaleLowerCase('tr-TR').endsWith('.zip') ? 'application/zip' : '');
      return {
        localId,
        id: localId,
        sessionId: attachmentSessionIdRef.current,
        key: '',
        filename: file.name,
        size: file.size,
        mimeType: fileType,
        progress: 0,
        status: 'uploading' as const,
      };
    });

    setAttachments((current) => [...current, ...nextAttachments]);
    nextAttachments.forEach((attachment, index) => uploadAttachment(attachment.localId, acceptedFiles[index]));
  }, [attachments.length, uploadAttachment]);

  const removeAttachment = (localId: string) => {
    setAttachments((current) => current.filter((attachment) => attachment.localId !== localId));
    setErrors((current) => ({ ...current, attachments: undefined, submit: undefined }));
  };

  const handleAttachmentInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    addAttachmentFiles(Array.from(event.target.files ?? []));
    event.target.value = '';
  };

  const resetQuoteRequest = () => {
    sessionStorage.removeItem(STORAGE_KEY);
    setDraft(emptyDraft);
    setStep(1);
    setErrors({});
    setModalState('form');
    setIsSubmitting(false);
    setQuoteRequestId('');
    setOtp('');
    setFeatureQuery('');
    setConfirmationResult(null);
    setPendingPhone('');
    setAttachments([]);
    attachmentSessionIdRef.current = crypto.randomUUID();
    destroyRecaptcha();
  };

  const closeModal = () => {
    if (modalState === 'success') {
      resetQuoteRequest();
    }
    onClose();
  };

  const sendFirebaseSms = async (phone: string): Promise<ConfirmationResult> => {
    destroyRecaptcha();
    const verifier = createRecaptchaVerifier();
    const auth = getFirebaseAuth();
    const e164Phone = `+${phone}`;
    return signInWithPhoneNumber(auth, e164Phone, verifier);
  };

  const getSafeFirebaseErrorDetails = (error: unknown) => {
    if (!error || typeof error !== 'object') return undefined;
    const details = error as { code?: unknown; message?: unknown; name?: unknown };
    return {
      code: typeof details.code === 'string' ? details.code : undefined,
      message: typeof details.message === 'string' ? details.message : undefined,
      name: typeof details.name === 'string' ? details.name : undefined,
    };
  };

  const reportSmsFailure = async (id: string, error: unknown, markFailed = true) => {
    const errorDetails = getSafeFirebaseErrorDetails(error);
    console.error('Quote SMS verification failed', {
      quoteRequestId: id,
      ...errorDetails,
    });
    try {
      await markQuoteSmsFailed(id, { error: errorDetails, markFailed });
    } catch (reportError) {
      console.error('Quote SMS failure status update failed', {
        quoteRequestId: id,
        message: reportError instanceof Error ? reportError.message : String(reportError),
      });
    }
  };

  const validateStep4 = () => {
    if (!validateStep(4)) return false;
    return true;
  };

  const handleSubmitClick = () => {
    if (!validateStep4()) return;
    setPhoneConfirmOpen(true);
  };

  const submit = async () => {
    if (!validateStep4() || attachments.some((attachment) => attachment.status !== 'uploaded') || !turnstileRef.current) {
      if (attachments.some((attachment) => attachment.status !== 'uploaded')) {
        setErrors((current) => ({ ...current, submit: 'Dosya yüklemeleri tamamlanmadan gönderemezsiniz.' }));
      }
      return;
    }
    setIsSubmitting(true);
    setErrors({});
    let startedQuoteRequestId = '';
    try {
      const turnstileToken = await getTurnstileToken(turnstileRef.current);
      const response = await startQuoteRequest({
        ...draft,
        phone: normalizeTurkishMobilePhone(draft.phone),
        email: normalizeEmail(draft.email),
        attachments: attachments.map(({ id, sessionId, key, filename, size, mimeType }) => ({
          id,
          sessionId,
          key,
          filename,
          size,
          mimeType,
        })),
        consent: draft.kvkkAcknowledged && draft.contactAcknowledged,
        kvkkDocumentVersion: acceptedLegalVersion.current,
        turnstileToken,
      });
      startedQuoteRequestId = response.quoteRequestId;
      setQuoteRequestId(response.quoteRequestId);
      setPendingPhone(response.phone);

      // Send SMS via Firebase Phone Auth
      const confirmation = await sendFirebaseSms(
        response.phone.startsWith('+') ? response.phone.slice(1) : response.phone
      );
      setConfirmationResult(confirmation);
      setModalState('otp');
    } catch (error) {
      if (error instanceof Error && 'code' in error && error.code === 'LEGAL_NOTICE_CHANGED') {
        setConsentAcknowledged(false);
        legalNotice.retry();
      }

      destroyRecaptcha();
      if (startedQuoteRequestId) {
        await reportSmsFailure(startedQuoteRequestId, error);
      }
      // API refusals (quota 429, upload cap 400, limiter 503) carry a stable machine `code` of their
      // own; it is not a Firebase code and must not be mapped to the SMS error text. The server
      // message is the user-facing explanation.
      const firebaseCode = error instanceof ApiError ? undefined : (error as { code?: string })?.code;
      if (error instanceof ApiError) {
        setErrors({ submit: error.message });
      } else if (firebaseCode) {
        setErrors({ submit: firebaseErrorMessage(firebaseCode) });
      } else {
        setErrors({ submit: error instanceof Error ? error.message : 'Teklif talebi başlatılamadı.' });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const verifyOtp = async () => {
    if (otp.length !== 6) {
      setErrors({ otp: '6 haneli kodu girin.' });
      return;
    }
    if (!confirmationResult || !turnstileRef.current) {
      setErrors({ otp: 'Doğrulama oturumu bulunamadı. Lütfen tekrar başlatın.' });
      return;
    }
    setIsSubmitting(true);
    setErrors({});
    try {
      const userCredential = await confirmationResult.confirm(otp);
      const firebaseIdToken = await userCredential.user.getIdToken();
      const turnstileToken = await getTurnstileToken(turnstileRef.current);
      const verifyResponse = await verifyQuoteOtp({ quoteRequestId, firebaseIdToken, turnstileToken });

      // Consent update must be queued BEFORE the conversion event so GTM's gated Ads tag sees `granted`.
      if (draft.marketingConsent) {
        grantMarketingConsentSignal();
      }
      pushVerifiedLeadSubmitEvent(verifyResponse, { projectType: draft.projectType, budget: draft.budget }, getAttribution());

      sessionStorage.removeItem(STORAGE_KEY);
      setModalState('success');
    } catch (error) {
      const firebaseCode = (error as { code?: string })?.code;
      if (firebaseCode) {
        setErrors({ otp: firebaseErrorMessage(firebaseCode) });
      } else {
        setErrors({ otp: error instanceof Error ? error.message : 'Kod doğrulanamadı.' });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const resendOtp = async () => {
    if (!pendingPhone) return;
    setIsSubmitting(true);
    setErrors({});
    setOtp('');
    try {
      const confirmation = await sendFirebaseSms(
        pendingPhone.startsWith('+') ? pendingPhone.slice(1) : pendingPhone
      );
      setConfirmationResult(confirmation);
    } catch (error) {
      destroyRecaptcha();
      if (quoteRequestId) {
        await reportSmsFailure(quoteRequestId, error, !confirmationResult);
      }
      const firebaseCode = (error as { code?: string })?.code;
      if (firebaseCode) {
        setErrors({ otp: firebaseErrorMessage(firebaseCode) });
      } else {
        setErrors({ otp: error instanceof Error ? error.message : 'Yeni kod gönderilemedi.' });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const currentStepContent = stepContent[step - 1];
  const panelCopy = useMemo(() => {
    if (modalState === 'success') {
      return {
        title: 'Talebiniz Alındı',
        eyebrow: 'Tamamlandı',
        description:
          'Talebiniz kaydedildi. Değerlendirme için gerekirse sizinle en fazla 5 arama denemesiyle iletişime geçilecek.',
      };
    }
    if (modalState === 'otp') {
      return {
        title: 'Telefonunuzu Doğrulayın',
        eyebrow: 'SMS Doğrulama',
        description:
          'Telefon numaranıza gönderilen 6 haneli kodu girerek teklif talebinizi güvenli şekilde tamamlayın.',
      };
    }
    return currentStepContent;
  }, [currentStepContent, modalState]);
  // The visible draft fields are already mirrored to sessionStorage above, so a forced reload keeps
  // them. Uploaded files and an in-progress SMS verification cannot be kept and are named as loss.
  const hasQuoteContent =
    draft.description.trim() !== '' ||
    draft.fullName.trim() !== '' ||
    draft.phone.trim() !== '' ||
    draft.email.trim() !== '' ||
    (draft.companyName ?? '').trim() !== '' ||
    draft.features.length > 0 ||
    attachments.length > 0 ||
    otp !== '';
  // The recovery prompt is worded in the active site language; the label and loss note follow it.
  const recoveryEnglish = useChunkRecoveryLanguage() === 'en';
  const quoteLoss = [
    attachments.length > 0
      ? recoveryEnglish
        ? `${attachments.length} file(s) must be uploaded again`
        : `${attachments.length} dosya yeniden yüklenmeli`
      : '',
    modalState === 'otp'
      ? recoveryEnglish
        ? 'SMS verification must be restarted'
        : 'SMS doğrulaması yeniden başlatılmalı'
      : '',
  ].filter(Boolean).join('; ');
  useUnsavedWork({
    label: recoveryEnglish ? 'Quote request' : 'Teklif talebi',
    dirty: open && modalState !== 'success' && hasQuoteContent,
    pending: isSubmitting,
    alreadyKept: true,
    lossNote: quoteLoss || undefined,
    // "Discard draft and reload" must not bring the stored fields back after the reload.
    onDiscard: () => {
      try {
        sessionStorage.removeItem(STORAGE_KEY);
      } catch {
        // storage unavailable: nothing was stored
      }
    },
  });

  if (!open) return null;

  return (
    <div className="animate-quote-backdrop fixed inset-0 z-[200] flex items-end justify-center bg-slate-950/90 md:items-center md:bg-slate-950/85 md:p-6 md:backdrop-blur-sm">
      <div className="absolute inset-0" onClick={closeModal} />
      <div className="animate-quote-panel relative flex h-[100dvh] w-full flex-col overflow-hidden border border-slate-700/80 bg-dark-900 shadow-2xl shadow-brand-950/50 md:h-auto md:max-h-[90vh] md:max-w-5xl md:rounded-3xl">
        <div className="pointer-events-none absolute inset-0 hidden md:block md:bg-[radial-gradient(circle_at_20%_10%,rgba(37,99,235,0.26),transparent_32%),radial-gradient(circle_at_86%_4%,rgba(124,58,237,0.16),transparent_30%)]" />
        <div className="relative flex min-h-0 flex-1 flex-col md:grid md:grid-cols-[0.82fr_1.18fr]">
          <aside className="hidden border-r border-slate-700/70 p-10 md:flex md:flex-col">
            <div>
              <p className="mb-4 text-base font-bold uppercase tracking-[0.22em] text-brand-400">Teklif Al</p>
              <h2 className="text-4xl font-black leading-tight text-white">
                Projenizi Birlikte <span className="text-brand-400">Netleştirelim</span>
              </h2>
              <p className="mt-5 max-w-sm text-sm leading-6 text-slate-300">
                {panelCopy.description}
              </p>
            </div>
            {modalState === 'form' && (
              <div className="mt-auto pt-10">
                <QuoteStepIndicator currentStep={step} totalSteps={4} labels={stepLabels} />
              </div>
            )}
          </aside>

          <section className="relative flex min-h-0 flex-1 flex-col">
            <header className="shrink-0 border-b border-slate-700/70 px-5 py-4 md:px-8">
              <div className="flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <p className="text-xs font-bold uppercase tracking-[0.18em] text-brand-400">{panelCopy.eyebrow}</p>
                  <h2 className="mt-1 text-xl font-black text-white md:text-2xl">{panelCopy.title}</h2>
                </div>
                <button
                  type="button"
                  onClick={closeModal}
                  className="rounded-xl border border-slate-700 bg-slate-900/70 p-2 text-slate-300 transition-colors hover:text-white"
                  aria-label="Kapat"
                >
                  <X size={20} />
                </button>
              </div>
              {modalState === 'form' && (
                <div className="mt-4 md:hidden">
                  <QuoteStepIndicator currentStep={step} totalSteps={4} labels={stepLabels} />
                </div>
              )}
            </header>

            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5 md:px-8 md:py-7">
              <div
                className="quote-content-shell"
                style={contentHeight === undefined ? undefined : { height: contentHeight }}
              >
                <div ref={animatedContentRef}>
                  {modalState === 'form' && (
                    <div key={`step-${step}`} className="animate-quote-step space-y-6">
                      {step === 1 && (
                        <div className="grid gap-3 md:grid-cols-3">
                          {projectTypes.map((type) => (
                            <QuoteOptionCard
                              key={type.value}
                              selected={draft.projectType === type.value}
                              icon={projectIcon(type.value)}
                              title={type.label}
                              description={type.description}
                              onClick={() => setDraft({ ...draft, projectType: type.value, features: [] })}
                            />
                          ))}
                        </div>
                      )}

                      {step === 2 && (
                        <div>
                          <Input
                            label="Özellik ara"
                            value={featureQuery}
                            onChange={(event) => setFeatureQuery(event.target.value)}
                            placeholder="Örn. ödeme, panel, bildirim"
                            className="mb-4 rounded-xl"
                          />
                          <div className="grid max-h-[44vh] gap-3 overflow-y-auto pr-1 sm:grid-cols-2 md:max-h-[48vh]">
                            {selectedCustomFeatures.map((feature) => (
                              <QuoteOptionCard
                                key={feature}
                                selected
                                icon={<Plus size={18} />}
                                title={feature}
                                onClick={() => toggleFeature(feature)}
                                className="p-3"
                              />
                            ))}
                            {visibleFeatures.map((feature) => (
                              <QuoteOptionCard
                                key={feature.label}
                                selected={draft.features.includes(feature.label)}
                                icon={<span className="text-lg leading-none">{feature.emoji}</span>}
                                title={feature.label}
                                onClick={() => toggleFeature(feature.label)}
                                className="p-3"
                              />
                            ))}
                          </div>
                          {visibleFeatures.length === 0 && (
                            <div className="mt-3 rounded-xl border border-slate-700 bg-slate-900/50 p-3">
                              {canAddCustomFeature ? (
                                <Button
                                  type="button"
                                  onClick={addCustomFeature}
                                  className="w-full justify-center gap-2 rounded-xl"
                                >
                                  <Plus size={16} />
                                  "{normalizedFeatureQuery}" özelliğini ekle
                                </Button>
                              ) : (
                                <p className="text-sm text-slate-400">
                                  {customFeatureError ?? 'Özellik adı girin.'}
                                </p>
                              )}
                            </div>
                          )}
                          {errors.features && <p className="mt-3 text-sm text-red-400">{errors.features}</p>}
                        </div>
                      )}

                      {step === 3 && (
                        <div className="space-y-4">
                          <Textarea
                            label="Proje özeti"
                            value={draft.description}
                            onChange={(event) => setField('description', event.target.value)}
                            error={errors.description}
                            rows={5}
                            placeholder="Hedeflerinizi, beklentilerinizi ve özel notlarınızı yazın."
                            className="rounded-xl"
                          />
                          <div className="flex items-center justify-between gap-3 text-xs">
                            <span
                              className={
                                draft.description.trim().length < DESCRIPTION_MIN_LENGTH ||
                                draft.description.length > DESCRIPTION_MAX_LENGTH
                                  ? 'text-red-400'
                                  : 'text-slate-400'
                              }
                            >
                              Min. {DESCRIPTION_MIN_LENGTH}, max. {DESCRIPTION_MAX_LENGTH} karakter
                            </span>
                            <span
                              className={
                                draft.description.length > DESCRIPTION_MAX_LENGTH ? 'font-semibold text-red-400' : 'text-slate-400'
                              }
                            >
                              {draft.description.length} / {DESCRIPTION_MAX_LENGTH}
                            </span>
                          </div>
                          <p className="text-xs leading-5 text-slate-400">
                            Lütfen proje özeti veya özel özellik alanlarında kimlik belgesi, ödeme bilgisi, sağlık
                            bilgisi veya gereksiz özel nitelikli kişisel veri paylaşmayın.
                          </p>
                          <div className="space-y-3">
                            <div
                              role="button"
                              tabIndex={0}
                              onClick={() => fileInputRef.current?.click()}
                              onKeyDown={(event) => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                  event.preventDefault();
                                  fileInputRef.current?.click();
                                }
                              }}
                              onDragEnter={(event) => {
                                event.preventDefault();
                                setIsDragActive(true);
                              }}
                              onDragOver={(event) => event.preventDefault()}
                              onDragLeave={(event) => {
                                event.preventDefault();
                                setIsDragActive(false);
                              }}
                              onDrop={(event) => {
                                event.preventDefault();
                                setIsDragActive(false);
                                addAttachmentFiles(Array.from(event.dataTransfer.files));
                              }}
                              className={`rounded-xl border border-dashed p-4 text-center transition-colors ${
                                isDragActive
                                  ? 'border-brand-400 bg-brand-500/10'
                                  : 'border-slate-700 bg-slate-900/50 hover:border-slate-500'
                              }`}
                            >
                              <input
                                ref={fileInputRef}
                                type="file"
                                className="hidden"
                                accept={ATTACHMENT_ACCEPT}
                                multiple
                                onChange={handleAttachmentInput}
                              />
                              <div className="flex flex-col items-center gap-2">
                                <UploadCloud className="text-brand-300" size={26} />
                                <div className="text-sm font-semibold text-white">Dosyaları sürükleyin veya seçin</div>
                                <div className="text-xs text-slate-400">
                                  Görsel, PDF veya ZIP. En fazla 30 dosya, dosya başına 30 MB.
                                </div>
                              </div>
                            </div>
                            {errors.attachments && <p className="text-sm text-red-400">{errors.attachments}</p>}
                            {attachments.length > 0 && (
                              <div className="grid gap-2">
                                {attachments.map((attachment) => (
                                  <div
                                    key={attachment.localId}
                                    className="flex items-center gap-3 rounded-xl border border-slate-700 bg-slate-900/50 p-3"
                                  >
                                    <div className="text-brand-300">{attachmentIcon(attachment.mimeType)}</div>
                                    <div className="min-w-0 flex-1">
                                      <div className="flex items-center justify-between gap-3">
                                        <p className="truncate text-sm font-semibold text-slate-100">{attachment.filename}</p>
                                        <span className="shrink-0 text-xs text-slate-400">{formatFileSize(attachment.size)}</span>
                                      </div>
                                      <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-slate-800">
                                        <div
                                          className={`h-full rounded-full transition-all duration-300 ${
                                            attachment.status === 'error' ? 'bg-red-500' : 'bg-brand-500'
                                          }`}
                                          style={{ width: `${attachment.status === 'error' ? 100 : attachment.progress}%` }}
                                        />
                                      </div>
                                      <p
                                        className={`mt-1 text-xs ${
                                          attachment.status === 'error' ? 'text-red-400' : 'text-slate-400'
                                        }`}
                                      >
                                        {attachment.status === 'uploaded'
                                          ? 'Yüklendi'
                                          : attachment.status === 'error'
                                            ? 'Yüklenemedi'
                                            : `Yükleniyor ${attachment.progress}%`}
                                      </p>
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() => removeAttachment(attachment.localId)}
                                      className="rounded-lg border border-slate-700 p-1.5 text-slate-400 transition-colors hover:border-red-400 hover:text-red-300"
                                      aria-label={`${attachment.filename} dosyasını kaldır`}
                                    >
                                      <X size={16} />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                          <div className="grid gap-4 md:grid-cols-2">
                            <Select
                              label="Tahmini bütçe"
                              value={draft.budget ?? ''}
                              onChange={(event) => setField('budget', event.target.value as QuoteBudgetRange)}
                              error={errors.budget}
                              className="rounded-xl"
                            >
                              <option value="">Seçiniz</option>
                              {budgetOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </Select>
                            <Select
                              label="Başlangıç zamanı"
                              value={draft.timeline ?? ''}
                              onChange={(event) => setField('timeline', event.target.value as QuoteTimeline)}
                              error={errors.timeline}
                              className="rounded-xl"
                            >
                              <option value="">Seçiniz</option>
                              {timelineOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </Select>
                          </div>
                        </div>
                      )}

                      {step === 4 && (
                        <div className="space-y-4">
                          <div className="grid gap-4 md:grid-cols-2">
                            <Input label="Ad Soyad" value={draft.fullName} onChange={(event) => setField('fullName', event.target.value)} error={errors.fullName} className="rounded-xl" />
                            <Input
                              ref={phoneInputRef}
                              label="Telefon"
                              value={draft.phone}
                              onChange={(event) => handlePhoneChange(event.target.value)}
                              error={errors.phone}
                              className="rounded-xl"
                              inputMode="tel"
                              placeholder="+90 5XX XXX XX XX"
                            />
                            <Input
                              label="E-posta"
                              value={draft.email}
                              onChange={(event) => setField('email', event.target.value)}
                              onBlur={handleEmailBlur}
                              error={errors.email}
                              className="rounded-xl"
                              type="email"
                              placeholder="ornek@sirket.com"
                            />
                            <Input label="Firma adı (Opsiyonel)" value={draft.companyName ?? ''} onChange={(event) => setField('companyName', event.target.value)} className="rounded-xl" />
                          </div>
                          <label className="flex gap-3 rounded-xl border border-slate-700 bg-slate-900/50 p-3 text-sm text-slate-300">
                            <input
                              type="checkbox"
                              disabled={!legalNotice.document}
                              checked={draft.kvkkAcknowledged && draft.contactAcknowledged}
                              onChange={(event) => setConsentAcknowledged(event.target.checked)}
                              className="mt-1 h-4 w-4 rounded border-slate-600 bg-slate-900 accent-brand-600"
                            />
                            <span>
                              Kişisel verilerimin teklif talebimin değerlendirilmesi, telefon numaramın doğrulanması ve
                              talebim hakkında tarafımla iletişime geçilmesi amaçlarıyla işlenmesini kabul ediyor,{' '}
                              <a
                                href="/kvkk-aydinlatma-metni"
                                target="_blank"
                                rel="noreferrer"
                                className="font-semibold text-brand-300 underline-offset-4 hover:underline"
                              >
                                Aydınlatma Metni
                              </a>
                              'ni okuduğumu beyan ediyorum.
                            </span>
                          </label>
                          {legalNotice.error && <p role="alert" className="text-sm text-red-400">Aydınlatma metni yüklenemedi. <button type="button" onClick={legalNotice.retry}>Tekrar dene</button></p>}
                          {errors.kvkkAcknowledged && <p className="text-sm text-red-400">{errors.kvkkAcknowledged}</p>}
                          <label className="flex gap-3 rounded-xl border border-slate-700 bg-slate-900/50 p-3 text-sm text-slate-300">
                            <input
                              type="checkbox"
                              checked={draft.marketingConsent}
                              onChange={(event) => setField('marketingConsent', event.target.checked)}
                              className="mt-1 h-4 w-4 rounded border-slate-600 bg-slate-900 accent-brand-600"
                            />
                            <span>
                              Reklam ve ölçümleme çerezleri aracılığıyla kampanya performansının ölçülmesi amacıyla
                              verilerimin işlenmesine izin veriyorum.
                            </span>
                          </label>
                          <div ref={turnstileRef} className="min-h-0" />
                        </div>
                      )}
                      {errors.submit && <p className="rounded-xl border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-200">{errors.submit}</p>}
                    </div>
                  )}

                  {modalState === 'otp' && (
                    <div key="state-otp" className="animate-quote-step mx-auto max-w-md space-y-6 py-6">
                      <p className="text-sm leading-6 text-slate-300">
                        Telefon numaranızı doğrulamak ve teklif talebinizi güvenli şekilde tamamlamak için 6 haneli SMS
                        doğrulama kodu gönderilecektir. Bu doğrulama mesajı reklam, kampanya veya pazarlama amacı taşımaz.
                      </p>
                      <QuoteOtpInput value={otp} onChange={setOtp} disabled={isSubmitting} error={errors.otp} />
                      <div ref={turnstileRef} className="min-h-0" />
                      <button
                        type="button"
                        onClick={resendOtp}
                        disabled={isSubmitting}
                        className="text-sm font-semibold text-brand-300 disabled:text-slate-500"
                      >
                        Kodu tekrar gönder
                      </button>
                    </div>
                  )}

                  {modalState === 'success' && (
                    <div key="state-success" className="animate-quote-step mx-auto flex max-w-md flex-col items-center py-12 text-center">
                      <div className="mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-brand-500 text-white shadow-lg shadow-brand-500/30">
                        <Check size={30} />
                      </div>
                      <h3 className="text-2xl font-black text-white">Talebiniz Alındı</h3>
                      <p className="mt-3 text-sm leading-6 text-slate-300">
                        Teklif talebiniz alınmıştır. Talebinizin değerlendirilmesi için gerekirse sizinle en fazla 5 arama
                        denemesiyle iletişime geçilecektir.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <footer className="shrink-0 border-t border-slate-700/70 bg-dark-900/95 px-5 py-4 md:px-8">
              {modalState === 'form' && (
                <div className="flex items-center justify-between gap-3">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setStep((current) => Math.max(1, current - 1))}
                    disabled={step === 1 || isSubmitting}
                    leftIcon={<ArrowLeft size={18} />}
                    className="rounded-xl"
                  >
                    Geri
                  </Button>
                  <Button
                    type="button"
                    onClick={step === 4 ? handleSubmitClick : canGoNext}
                    isLoading={isSubmitting}
                    rightIcon={step === 4 ? <Send size={18} /> : undefined}
                    className="min-w-32 rounded-xl"
                  >
                    {step === 4 ? 'Doğrula ve Gönder' : 'Devam'}
                  </Button>
                </div>
              )}
              {modalState === 'otp' && (
                <Button type="button" onClick={verifyOtp} isLoading={isSubmitting} className="w-full rounded-xl" rightIcon={<Send size={18} />}>
                  Doğrulayıp Gönder
                </Button>
              )}
              {modalState === 'success' && (
                <Button type="button" onClick={closeModal} className="w-full rounded-xl">
                  Kapat
                </Button>
              )}
            </footer>
          </section>
        </div>

        <ConfirmDialog
          isOpen={phoneConfirmOpen}
          variant="info"
          title="Telefon numaranız doğru mu?"
          message={`${formatTurkishMobilePhone(draft.phone)} numarasına 6 haneli SMS doğrulama kodu gönderilecektir. Numara doğru değilse lütfen düzeltin.`}
          confirmLabel="Evet, gönder"
          cancelLabel="Hayır, düzelt"
          isLoading={isSubmitting}
          onConfirm={() => {
            setPhoneConfirmOpen(false);
            submit();
          }}
          onCancel={() => {
            setPhoneConfirmOpen(false);
            requestAnimationFrame(() => phoneInputRef.current?.focus());
          }}
        />

        {/* Keep Firebase's invisible reCAPTCHA mounted without letting it affect modal layout. */}
        <div
          ref={recaptchaContainerRef}
          id="firebase-recaptcha-container"
          className="pointer-events-none absolute h-0 w-0 overflow-hidden opacity-0"
          aria-hidden="true"
        />
      </div>
    </div>
  );
};

export default QuoteRequestModal;
